import React, { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import axios from "axios";

const Auth0Callback = () => {
  const { isAuthenticated, isLoading, getIdTokenClaims, user, error } = useAuth0();
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasRun = useRef(false);

  useEffect(() => {
    console.log('Auth0Callback state:', { isAuthenticated, isLoading, hasUser: !!user, hasRun: hasRun.current, error });

    if (hasRun.current || isLoading) return;

    const handleCallback = async () => {
      if (isAuthenticated && user) {
        hasRun.current = true;
        console.log('Processing Auth0 callback for user:', user.email);

        try {
          // Get the ID token from Auth0
          const tokenClaims = await getIdTokenClaims();
          const idToken = tokenClaims.__raw;

          // Send ID token to your backend session endpoint
          const sessionEndpoint = process.env.REACT_APP_AUTH_SESSION_ENDPOINT;
          console.log('Calling session endpoint:', sessionEndpoint);

          const { data } = await axios.post(sessionEndpoint, {
            idToken: idToken,
          });

          console.log('Session endpoint response:', data);

          // Extract platform user data from response
          // Backend may return: data.platformUser, data.user, or flat structure
          const platformUserData = data.platformUser || data.user || data;

          // Store user data in AuthContext
          // Convert boolean flags to strings to match existing app logic
          const userDataToStore = {
            idToken: idToken,
            auth0Sub: user.sub,
            name: user.name || platformUserData.name,
            email: user.email || platformUserData.email,
            picture: user.picture || platformUserData.picture,
            emailVerified: user.email_verified,
            platformUser: {
              _id: platformUserData._id,
              email: platformUserData.email,
              roles: platformUserData.roles || [],
              isActive: String(platformUserData.isActive || false),
              isDeveloper: String(platformUserData.isDeveloper || false),
              isAdmin: String(platformUserData.isAdmin || false),
              isSuperAdmin: String(platformUserData.isSuperAdmin || false),
            },
          };

          console.log('Logging in user and redirecting...');
          login(userDataToStore);
          navigate("/");
        } catch (err) {
          console.error("Authentication error:", err);
          console.error("Error details:", err.response?.data || err.message);
          alert(`Authentication failed: ${err.response?.data?.message || err.message}`);
          navigate("/");
        }
      } else if (!isLoading) {
        console.warn('Not authenticated after loading completed');
        if (error) {
          console.error('Auth0 error:', error);
          alert(`Auth0 error: ${error.message}`);
        }
      }
    };

    handleCallback();
  }, [isAuthenticated, isLoading, user, getIdTokenClaims, login, navigate, error]);

  if (isLoading) {
    return <p>Processing authentication...</p>;
  }

  if (error) {
    return (
      <div>
        <p>Authentication error: {error.message}</p>
        <button onClick={() => navigate("/")}>Return to home</button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div>
        <p>Not authenticated. Redirecting...</p>
        <button onClick={() => navigate("/")}>Return to home</button>
      </div>
    );
  }

  return <p>Completing login via Auth0...</p>;
};

export default Auth0Callback;
